import React, {useContext, useEffect, useRef} from 'react';

// Reducer
import {AppContext} from '../contexts/context';

// containers
import {recordId} from './RecordData'
import {idbGetData} from "./indexedDB"

// Components
import {convertArraysToObjects, removeQueryParams} from '../components/AppUtils';
import {resolveEnv} from './Utils';

// Firebase
import {storage} from "./Firebase"
import {getDownloadURL, ref} from "firebase/storage";

// Tools
import LZString from 'lz-string';
import {getAppUrl} from "./AppUrl";
import {saveDemoMode} from "../models/demoMode";
import {Capacitor} from "@capacitor/core";
import {App} from "@capacitor/app";
import * as fflate from "fflate";

function isSafari() {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') &&
    !ua.includes('chrome') &&
    !ua.includes('chromium') &&
    !ua.includes('android');
};

// Reason of a failed schema import mapped to the text shown in the error dialog.
// The reason tells the user what to do next, so every failure path has to pick
// the one that actually applies.
const schemaErrorTexts = {
  notFound: `The schema for this configuration does not exist on the server. Ask for a new link.`,
  unreachable: `The schema could not be downloaded, the server was not reachable. Check your internet connection and try again.`,
  server: `The schema could not be downloaded, the server reported an error. Try again later.`,
  invalid: `The downloaded schema is damaged and could not be read. Ask for a new link.`,
};

const DataImport = () => {
  // global context
  const [state, dispatch] = useContext(AppContext);
  const [activeSearchParams, setActiveSearchParams] = React.useState(null);
  // Status of the schema import as an object, so state and failure reason are
  // always committed together.
  const [importSchemaStatus, setImportSchemaStatus] = React.useState({state: 'pending'});
  // Firebase storage reference
  const storageRef = ref(storage);


  // Returns the cached schema or null if it is not cached (yet). A broken cache
  // is not an import error, the download takes over.
  async function loadSchemaFromIdb(schemaId) {
    try {
      const data = await idbGetData(schemaId);
      if (!data) {
        console.debug("DataImport | IndexedDB: Schema not cached:", schemaId);
        return null;
      }
      console.log(`DataImport | IndexedDB: Schema with ID ${schemaId} from IndexedDB:`, data);
      return data;
    } catch (error) {
      console.debug("DataImport | IndexedDB: idb error", error);
      return null;
    }
  }

  // Loads the schema from the IndexedDB cache, falling back to Firebase storage.
  // Every path ends in exactly one terminal status.
  async function importSchema(schemaId) {
    setImportSchemaStatus({state: 'pending'});

    const cachedSchema = await loadSchemaFromIdb(schemaId);
    if (cachedSchema) {
      dispatch({type: 'data/S/updated', value: cachedSchema});
      setImportSchemaStatus({state: 'done'});
      return;
    }

    if (!navigator.onLine) {
      setImportSchemaStatus({state: 'offline'});
      return;
    }

    console.debug(`DataImport | importSchema: Download started for ${schemaId} ... `);
    let url;
    try {
      url = await getDownloadURL(ref(storageRef, `${schemaId}/schema.json`));
      console.debug("DataImport | importSchema: URL", url);
    } catch (error) {
      console.error("DataImport | importSchema: getDownloadURL failed:", error);
      dispatch({
        type: 'app/ui/snackbar/show',
        message: `Failed to retrieve data ${schemaId}`,
        severity: 'warning'
      });
      setImportSchemaStatus({
        state: 'failed',
        reason: error?.code === 'storage/object-not-found' ? 'notFound' : 'unreachable'
      });
      return;
    }

    let response;
    try {
      response = await fetch(url);
    } catch (error) {
      // fetch only rejects on transport level errors: no connection, DNS, or a
      // response blocked by CORS.
      console.error('DataImport | importSchema: fetch failed:', error);
      setImportSchemaStatus({state: 'failed', reason: 'unreachable'});
      return;
    }

    // fetch resolves on error status codes as well, so they have to be checked.
    if (!response.ok) {
      console.error(`DataImport | importSchema: fetch returned ${response.status}`, response);
      setImportSchemaStatus({
        state: 'failed',
        reason: response.status === 404 ? 'notFound' : 'server',
        detail: `HTTP ${response.status}`
      });
      return;
    }

    let schema;
    try {
      schema = await response.json();
    } catch (error) {
      console.error('DataImport | importSchema: parsing response failed:', error);
      setImportSchemaStatus({state: 'failed', reason: 'invalid'});
      return;
    }

    console.debug("DataImport | importSchema: Schema downloaded ... ");
    console.debug("DataImport | importSchema: Schema:", schema);
    // const nfcSchemaCompressed = fflate.compressSync(schema, { level: 6, mem: 8 });
    dispatch({type: 'data/S/updated', value: schema});
    // dispatch({type: 'data/S/updatedCompressed', value: nfcSchemaCompressed});
    setImportSchemaStatus({state: 'done'});
  }

  function processDataImport(obj) {
    const requiredSchema = ["S"];
    const requiredParameters = ["P", "PP", "PR"];

    console.debug(`DataImport | processDataImport for`, obj);

    // Error handling
    if (!requiredSchema.some(key => obj.hasOwnProperty(key))) {
      console.error(`DataImport | processDataImport failed, schema missing!`, obj);
      return false;
    }

    if (!requiredParameters.some(key => obj.hasOwnProperty(key))) {
      console.error(`DataImport | processDataImport failed, some parameters missing!`, obj);
      return false;
    }

    if (Object.keys(obj).length < 2) {
      console.error(`DataImport | processDataImport failed, not enough data included!`, obj);
      return false;
    }

    // Process Import
    for (const key in obj) {
      console.debug(`DataImport | processDataImport for key`, key);
      if (obj.hasOwnProperty(key)) {
        if (key in recordId) {
          if (key === 'S') {
            // Runs on its own, the remaining keys do not depend on the schema.
            // Failures are reported through importSchemaStatus.
            importSchema(obj[key]);
          } else {
            let subObj = {[key]: obj[key]};
            console.debug(`DataImport | processDataImport: subObj`, subObj);

            dispatch({type: `data/${key}/imported`, value: subObj});
            dispatch({type: `ndef/control/updateScanTime`});
            if (!(Object.keys(obj[key]).length === 0 && obj[key].constructor === Object)) {
              console.debug(`DataImport | processDataImport: Dispatch data/${key}/updated with value`, obj[key]);
            } else {
              console.warn(`DataImport | processDataImport: ${key} is empty!`);
            }
          }
        } else {
          console.warn(`DataImport | processDataImport: failed, unknown key not processed!`, obj);
        }
      }
    }
    return true;
  }

  // Activate demo mode (?demo=true): only persist the global status. The demo
  // device data is loaded lazily on the simulated scan (demoModeReadSuccess),
  // not here, so the regular scan flow stays in charge.
  const activateDemoMode = () => {
    console.debug('DataImport | activateDemoMode');
    saveDemoMode(true);
    dispatch({type: 'app/settings/demoMode/toggle', value: true});
    dispatch({
      type: 'app/ui/snackbar/show',
      message: 'Demo mode activated.',
      severity: 'success'
    });
  }

  const importDataByKeys = (searchParams) => {
    let result = true;

    console.debug('DataImport | importDataByKeys: Try to restore data from URL:', searchParams)
    var compressedDataString = searchParams.get('D')
    if (compressedDataString === null) {
      console.error('Error: compressedDataString:', compressedDataString)
      return false;
    }
    console.debug('DataImport | importDataByKeys: compressedDataString', compressedDataString)
    var dataImportString = LZString.decompressFromEncodedURIComponent(compressedDataString);

    if (dataImportString === null || (typeof dataImportString === "string" && dataImportString.trim() === "")) {
      console.error('DataImport | importDataByKeys: decompress failed', dataImportString)
      return false;
    }

    console.debug('DataImport | importDataByKeys: dataString', dataImportString)
    var dataImportObject = JSON.parse(dataImportString);
    if (dataImportObject !== null) {
      console.debug("DataImport | importDataByKeys: dataImportObject", dataImportObject);
      var dataimportRestoredObject = convertArraysToObjects(dataImportObject)

      if (dataimportRestoredObject !== null) {
        console.debug("DataImport | importDataByKeys: dataimportRestoredObject", dataimportRestoredObject);
        result = processDataImport(dataimportRestoredObject);
      } else {
        console.error('DataImport | importDataByKeys: Convert Array failed!')
        result = false;
      }
    } else {
      console.error('DataImport | importDataByKeys: Parsing data failed!')
      result = false;
    }

    return result;
  }

  // Support Universal links on iOS
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') return;
    App.addListener('appUrlOpen', (event) => {
      console.log("AppUrlListener | event.url: ", event.url, " |")
      const universalLinkUrl = new URL(event.url);
      setActiveSearchParams(universalLinkUrl.searchParams);
    });
  }, []);

  // Initially check uri for import data
  useEffect(() => {
      // Has already query data to import?
      const myURL = getAppUrl();
    setActiveSearchParams(myURL.searchParams);
  }, []);

  useEffect(() => {
    // Has query data to import?
    if (!activeSearchParams) return;

    // Environment override via ?ENV=dev|stg. The parameters of a universal link
    // are not part of window.location, so the env has to be resolved again here
    // (App.js only sees the plain app url on startup).
    if (activeSearchParams.has('ENV')) {
      const env = resolveEnv(activeSearchParams);
      console.debug('DataImport | UseEffect: env from url parameter:', env);
      dispatch({type: 'app/env/set', value: env});
    }

    // Demo mode activation via ?demo=true (independent of NFC availability)
    if (activeSearchParams.get('demo') === 'true') {
      activateDemoMode();
      setActiveSearchParams(null);
      removeQueryParams();
      return;
    }

    if (!activeSearchParams.has('D')) return;
    if (activeSearchParams.has('N')) {
      dispatch({
        type: 'app/ui/importConfigName/set',
        value: activeSearchParams.get('N')
      });
    }
    console.debug(`DataImport | UseEffect`);

    // Only import data on devices with nfc available
    if (isSafari() && Capacitor.getPlatform() !== 'ios') {
      console.log("DataImport | UseEffect: NFC not available, aborting import!")
      // TODO Show info to user
      return;
    }

    // Import initializion error?
    if (!importDataByKeys(activeSearchParams)) {
      dispatch({
        type: 'app/ui/dialog/error',
        title: `Error importing data`,
        text: `An error has occurred while processing the data. Try again or ask for a new link.`
      });
    } else {
      dispatch({type: 'app/ui/dialog/default/show', value: 'import'});
    }

    setActiveSearchParams(null);
    removeQueryParams();
  }, [activeSearchParams]);

  useEffect(() => {
    console.debug("DataImport | switch:", importSchemaStatus)
    switch (importSchemaStatus.state) {
      case 'failed': {
        const errorText = schemaErrorTexts[importSchemaStatus.reason]
          ?? `The schema could not be loaded.`;
        dispatch({type: 'app/ui/dialog/default/close'});
        dispatch({
          type: 'app/ui/dialog/error',
          title: `Error importing data`,
          text: importSchemaStatus.detail
            ? `${errorText} (${importSchemaStatus.detail})`
            : errorText
        });
        break;
      }
      case 'offline':
        dispatch({type: 'app/ui/dialog/default/close'});
        dispatch({
          type: 'app/ui/dialog/error',
          title: `Error importing data`,
          text: `The data is out of date and needs to be updated. Establish an online connection and try again.`
        });
        break;
      case 'done':
        dispatch({type: 'app/ui/dialog/default/show', value: 'import'});
        break;
      case 'pending':
      default:
        break;
    }
  }, [importSchemaStatus]);

  return (
    <div>
    </div>
  );

}

export default DataImport;
